'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useProjectNavigationStore } from '@/store/projectNavigationStore'
import { ProjectBootScreen } from '@/components/ProjectBootScreen'

// Last-resort backstop only — the popstate and pathname guards below handle
// the known cancellation paths. This just guarantees the overlay can never
// permanently lock the app if a navigation dies in a way we didn't foresee.
const NAVIGATION_STUCK_MS = 30_000

/**
 * Covers the current route with the project boot screen the instant a user
 * opens a project. Lives in AppShell so it survives the projects page unmount
 * while the editor chunk downloads. The project editor page ends the
 * navigation when it mounts; the guards here end it when the navigation is
 * abandoned instead (Back button, route error, redirect elsewhere).
 */
export function ProjectNavigationOverlay() {
  const pathname = usePathname()
  const isNavigating = useProjectNavigationStore(state => state.isNavigating)
  const originPathname = useProjectNavigationStore(state => state.originPathname)
  const endNavigation = useProjectNavigationStore(state => state.endNavigation)

  // Back/forward during the in-flight navigation returns to a route that is
  // already interactive — drop the overlay immediately.
  useEffect(() => {
    if (!isNavigating) return
    const onPopState = () => endNavigation()
    // Opening a project is a document navigation; Back can restore this page from the
    // back/forward cache with the overlay still up.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) endNavigation()
    }
    window.addEventListener('popstate', onPopState)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('popstate', onPopState)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [isNavigating, endNavigation])

  // If the route commits somewhere that is not a project editor (redirect,
  // error boundary fallback route), the editor's own endNavigation will never
  // run — clear here.
  useEffect(() => {
    if (!isNavigating) return
    if (pathname !== originPathname && !pathname.startsWith('/p/')) endNavigation()
  }, [pathname, isNavigating, originPathname, endNavigation])

  useEffect(() => {
    if (!isNavigating) return
    const timeoutId = window.setTimeout(() => endNavigation(), NAVIGATION_STUCK_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isNavigating, endNavigation])

  if (!isNavigating) return null

  return (
    <div className="fixed inset-0 z-[100]" aria-busy="true" aria-live="polite">
      <ProjectBootScreen />
    </div>
  )
}
